import { Button } from "@/components/ui/button";
import { PaymentType } from "@/lib/validations/schema";
import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import { PaymentDialog } from "./CapturePayment";
import { PaymentDetail } from "pages/GenericOrderDetailsPage/types/order";
import { TooltipGlobal } from "components/GlobalTooltip";
import PaymentReview from "pages/GenericOrderDetailsPage/components/PaymentAcceptReject";

export default function Payments({ payments, payable, paid, balance, paymentButtonState }:
    {
        payments: PaymentDetail[];
        payable: number;
        paid: number;
        balance: number;
        paymentButtonState: {
            disabled: boolean;
            visible: boolean;
        },
    }
) {
    const tablePaymentData = payments.map((item) => ({
        id: item.acknowledgement_no.toString(),
        acknowledgement: item.acknowledgement_no.toString(),
        createdBy: item?.fname,
        creationDate: new Date(item.created_at).toLocaleDateString(),
        amount: Number(item.amount),
        receipt: item.receipt || null,
        paymentMode: item?.mode as string,
        paymentStatus: item?.status_by_AD2 as string,
        supportingDoc: item.supporting_doc || null,
        paymentId: item?.id.toString(),
    }));
    const paymentTableColumn: ColumnDef<PaymentType>[] = [
        {
            accessorKey: 'acknowledgement',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title='Acknowledgement' />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {row.getValue("acknowledgement")}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            accessorKey: 'creationDate',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title='Date Created' />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex flex-col  justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {row.getValue("creationDate")}
                        </p>
                        <p className="">
                            Created by {row.original.createdBy || "N/A"}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            accessorKey: 'amount',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title='Amount' />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {row.getValue("amount")}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            accessorKey: 'paymentMode',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title='Payment Mode' />
            ),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-between gap-1.5">
                        <p className="max-w-[200px] whitespace-normal break-words">
                            {row.getValue("paymentMode")}
                        </p>
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            accessorKey: 'paymentStatus',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title='Payment Status' />
            ),
            cell: ({ row }) => {
                const receiptUrl = row.original.receipt;
                const supportingDoc = row.original.supportingDoc || {};
                // const supportingDocUrl = row.original.supporting_doc;
                // const handleApprove = async (status_AD2: string, notes: string) => {
                //   setLoading(true);
                //   const auth = localStorage.getItem("authToken");
                //   if (!auth) return;
                //   const authToken = JSON.parse(auth);
                //   const payload: AxiosRequestConfig<POSTeditPaymentStatusRequestType> =
                //     {
                //       headers: { Authorization: `Bearer ${authToken}` },
                //       data: {
                //         order_id: Number(id),
                //         ack_no: row.original.acknowledgement,
                //         status_AD2,
                //         notes,
                //       },
                //     };
                //   try {
                //     const response = await PaymentStatusAPI(payload);
                //     if (response?.status === 200) {
                //       setLoading(false);
                //       if (status_AD2 === "rejected") {
                //         toast.success("Payment rejected!!");
                //       } else toast.success("Payment approved successfully");
                //     }
                //   } catch (error) {
                //     toast.error("Failed to update partner details");
                //     setLoading(false);
                //   }
                // };
                if (row?.original?.paymentStatus === "accept") {
                    return (
                        <div className="flex items-center gap-1.5">
                            <p className="max-w-[200px] py-1 bg-[#E1FFE2] px-2 rounded-md text-[#00A82D] whitespace-normal break-words">
                                Approved
                            </p>
                            <button
                                onClick={() => window.open(row.original.receipt.url, "_blank")}
                                className=""
                            >
                                <svg
                                    width="18"
                                    height="12"
                                    viewBox="0 0 18 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.3221 5.28375C16.5501 5.60325 16.6641 5.76375 16.6641 6C16.6641 6.237 16.5501 6.39675 16.3221 6.71625C15.2976 8.15325 12.6808 11.25 9.16406 11.25C5.64656 11.25 3.03056 8.1525 2.00606 6.71625C1.77806 6.39675 1.66406 6.23625 1.66406 6C1.66406 5.763 1.77806 5.60325 2.00606 5.28375C3.03056 3.84675 5.64731 0.75 9.16406 0.75C12.6816 0.75 15.2976 3.8475 16.3221 5.28375Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M11.4141 6C11.4141 5.40326 11.177 4.83097 10.7551 4.40901C10.3331 3.98705 9.7608 3.75 9.16406 3.75C8.56733 3.75 7.99503 3.98705 7.57307 4.40901C7.15112 4.83097 6.91406 5.40326 6.91406 6C6.91406 6.59674 7.15112 7.16903 7.57307 7.59099C7.99503 8.01295 8.56733 8.25 9.16406 8.25C9.7608 8.25 10.3331 8.01295 10.7551 7.59099C11.177 7.16903 11.4141 6.59674 11.4141 6Z"
                                        stroke="#165FE3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    );
                }
                if (row?.original?.paymentStatus === "reject") {
                    return (
                        <div className="flex items-center gap-1.5">
                            <p className="max-w-[200px] bg-red-200 px-2 py-1 flex items-center gap-x-2 rounded-md text-red-500 whitespace-normal break-words">
                                Rejected
                                <TooltipGlobal
                                    content={row?.original?.paymentStatus}
                                    triggerClassName=""
                                    contentClassName="bg-[#000] border-none text-white-A700"
                                >
                                    <button>
                                        <svg
                                            width="13"
                                            height="12"
                                            viewBox="0 0 13 12"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M6.5 0.09375C5.33186 0.09375 4.18994 0.440146 3.21867 1.08913C2.24739 1.73812 1.49037 2.66055 1.04334 3.73978C0.596309 4.819 0.479346 6.00655 0.70724 7.15225C0.935134 8.29795 1.49765 9.35035 2.32365 10.1763C3.14966 11.0024 4.20205 11.5649 5.34775 11.7928C6.49345 12.0207 7.681 11.9037 8.76023 11.4567C9.83945 11.0096 10.7619 10.2526 11.4109 9.28134C12.0599 8.31006 12.4063 7.16815 12.4063 6C12.4046 4.43407 11.7818 2.93276 10.6745 1.82548C9.56725 0.718198 8.06593 0.0954036 6.5 0.09375ZM6.27284 2.81971C6.40762 2.81971 6.53938 2.85968 6.65145 2.93456C6.76353 3.00945 6.85087 3.11588 6.90245 3.24041C6.95403 3.36493 6.96753 3.50196 6.94123 3.63415C6.91494 3.76635 6.85003 3.88778 6.75472 3.98309C6.65942 4.0784 6.53799 4.1433 6.40579 4.1696C6.27359 4.19589 6.13657 4.1824 6.01204 4.13082C5.88752 4.07924 5.78108 3.99189 5.7062 3.87982C5.63132 3.76775 5.59135 3.63599 5.59135 3.5012C5.59135 3.32046 5.66315 3.14712 5.79095 3.01932C5.91876 2.89151 6.0921 2.81971 6.27284 2.81971ZM6.95433 9.18029C6.71334 9.18029 6.48222 9.08456 6.31181 8.91415C6.14141 8.74374 6.04568 8.51262 6.04568 8.27164V6C5.92518 6 5.80962 5.95213 5.72442 5.86693C5.63921 5.78173 5.59135 5.66617 5.59135 5.54567C5.59135 5.42518 5.63921 5.30962 5.72442 5.22442C5.80962 5.13921 5.92518 5.09135 6.04568 5.09135C6.28667 5.09135 6.51779 5.18708 6.68819 5.35748C6.8586 5.52789 6.95433 5.75901 6.95433 6V8.27164C7.07482 8.27164 7.19038 8.3195 7.27559 8.4047C7.36079 8.48991 7.40866 8.60547 7.40866 8.72596C7.40866 8.84646 7.36079 8.96202 7.27559 9.04722C7.19038 9.13242 7.07482 9.18029 6.95433 9.18029Z"
                                                fill="#FF0000"
                                            />
                                        </svg>
                                    </button>
                                </TooltipGlobal>
                            </p>
                        </div>
                    );
                }
                return (
                    <div className="flex flex-col gap-2">
                        {row?.original?.paymentMode === "offline" ? (
                            <PaymentReview
                                paymentId={row?.original?.paymentId as string}
                                paymentProofUrl={receiptUrl}
                                supportingDocUrl={supportingDoc}
                                amount={row?.original?.amount}
                            >
                                <button
                                    style={{
                                        display: "flex",
                                        padding: "10px 20px",
                                        backgroundColor: "inherit",
                                        color: "#333333",
                                        textDecoration: "none",
                                        borderRadius: "5px",
                                        border: "1px solid #333333",
                                        fontWeight: "bold",
                                        textAlign: "center",
                                        cursor: "pointer",
                                        transition: "background-color 0.3s ease",
                                    }}
                                >
                                    Review
                                </button>
                            </PaymentReview>
                        ) : (
                            <>
                                <p
                                    //   style={{
                                    //     backgroundColor: order_status_options.find(
                                    //       (status) => orderdetails?.status === status.value,
                                    //     )?.bgColor,
                                    //     color: order_status_options.find(
                                    //       (status) => orderdetails?.status === status.value,
                                    //     )?.textColor,
                                    //   }}
                                    className="py-[4px] text-sm leading-4 capitalize font-medium  px-2 text-center rounded-md"
                                >
                                    {/* {orderdetails?.payment_status} */}
                                </p>
                            </>
                        )}
                    </div>
                );
            },
            enableSorting: false,
        },
    ]
    const isFullypaid = paid >= payable;
    return (
        <div className="px-3 pb-5 rounded-xl gap-5 flex flex-col">
            <div className="flex justify-between">
                <div className="flex gap-5 items-center">
                    <p className="leading-4 font-semibold">Payments</p>
                    <p className={`py-[4px] text-sm leading-4 capitalize font-medium ${isFullypaid ? ' bg-green-200 text-green-700' : 'bg-amber-200'}  px-2 text-center rounded-md`}>
                        {isFullypaid ? "Paid" : "Pending"}
                    </p>
                </div>
                {
                    payable != paid && (
                        <PaymentDialog>
                            <Button disabled={(paymentButtonState.disabled || payable === paid)} variant='ghost' className="rounded-lg border border-blue-700 text-white-A700 bg-blue-700">
                                Capture payment
                            </Button>
                        </PaymentDialog>
                    )
                }
            </div>
            <div className="flex flex-col gap-4">
                {/* Total Payable Row */}
                <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-lg">
                        Total Payable
                    </span>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-sm">INR</span>
                        <span className="text-gray-800 text-lg">
                            {payable}
                        </span>
                    </div>
                </div>

                {/* Total Paid Row */}
                <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-lg">Total Paid</span>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-sm">INR</span>
                        <span className="text-gray-800 text-lg">
                            {paid}
                        </span>
                    </div>
                </div>

                {/* Balance Row */}
                <div className={`flex justify-between items-center ${isFullypaid ? 'bg-green-100 text-green-700': 'bg-red-50 text-red-500 '}  p-4 rounded`}>
                    <span className="text-lg">Balance</span>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-sm">INR</span>
                        <span className="text-gray-800 text-lg">
                            {balance}
                        </span>
                    </div>
                </div>
            </div>
            <DataTable
                columns={paymentTableColumn}
                data={tablePaymentData}
                columnsBackground="#EEEEEE"
                isPaginationVisible={tablePaymentData.length > 10}
                isToolbarVisible={false}
            />
        </div>
    )
}
